import { Center, Loader } from '@mantine/core';
import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks';

interface AdminRouteProps {
  children: ReactNode;
}

/**
 * Redirects to `/login` if signed out, or to `/` if signed in but not an
 * admin. Gating is based solely on the `admin` custom claim (`isAdmin` from
 * `useAuth`), never the Firestore `AppUser.isAdmin` mirror field.
 */
export const AdminRoute = ({ children }: AdminRouteProps) => {
  const { firebaseUser, appUser, isAdmin, loading, isAdminLoading } = useAuth();
  const location = useLocation();

  // Unlike `ProtectedRoute`, this component's allow/redirect decision
  // depends on `isAdmin`, so it must also wait for the claim fetch —
  // `loading` alone (auth state + profile doc) isn't enough here.
  if (loading || isAdminLoading) {
    return (
      <Center mih="100vh">
        <Loader />
      </Center>
    );
  }

  if (!firebaseUser) {
    return <Navigate replace state={{ from: location.pathname }} to="/login" />;
  }

  // Force a password change before anything else if it's outstanding (first
  // login after admin-creation, or after an admin password reset).
  if (appUser?.mustChangePassword && location.pathname !== '/account/change-password') {
    return <Navigate replace to="/account/change-password" />;
  }

  if (!isAdmin) {
    return <Navigate replace to="/" />;
  }

  return <>{children}</>;
};
